import React, { useEffect, useState } from "react";
import { getAllUsers } from "../../services/userService";
import { User } from "../../types/User";
import "./AdminUserSelector.scss";

interface AdminUserSelectorProps {
  onUserSelected: (user: User) => void;
}

const AdminUserSelector: React.FC<AdminUserSelectorProps> = ({
  onUserSelected,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const fetchedUsers = await getAllUsers();
        setUsers(fetchedUsers);
        setFilteredUsers(fetchedUsers);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);

    if (newSearchTerm === "") {
      setFilteredUsers(users);
    } else {
      const lowercasedSearchTerm = newSearchTerm.toLowerCase();
      const filtered = users.filter(
        (user) =>
          user.email?.toLowerCase().includes(lowercasedSearchTerm) ||
          user.uid.toLowerCase().includes(lowercasedSearchTerm)
      );
      setFilteredUsers(filtered);
    }
  };

  const handleUserSelect = (user: User) => {
    onUserSelected(user);
    setSearchTerm("");
    setFilteredUsers([]);
  };

  return (
    <div className="admin-user-selector">
      <input
        type="text"
        name="admin-user-selector"
        placeholder="Search users by email or UID"
        value={searchTerm}
        onChange={handleInputChange}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls="users-dropdown-list"
      />
      {isLoading && <p className="loading-message">Loading users...</p>}
      {error && (
        <div className="alert alert-danger">
          Error fetching users: {error.message}
        </div>
      )}
      {searchTerm && !isLoading && !error && (
        <ul id="users-dropdown-list" className="users-dropdown" role="listbox">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <li
                key={user.uid}
                className="user-item"
                onClick={() => handleUserSelect(user)}
                role="option"
                aria-selected={false}
              >
                {user.email} ({user.uid})
              </li>
            ))
          ) : (
            <li className="no-users-message" aria-live="polite">
              No users found.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AdminUserSelector;
